import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { diffDaysIST, istDayKey, todayIST } from "../../lib/datetime";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, Check, X, Loader2, RefreshCw, Merge } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "../../lib/expenseOptions";
import { usePendingApprovals } from "../../context/PendingApprovalsContext";
import StickyHeading from "../../components/common/StickyHeading";

/**
 * Each module keeps its own approve/reject endpoints — this page does not
 * introduce a write path of its own, so the rule stays enforced in one place
 * per module. Tour plans call their negative action "send-back" rather than
 * "reject", which is a real difference in meaning, not an inconsistency to
 * paper over: a plan comes back for revision, an expense claim does not.
 */
const MODULES = {
  expense: {
    label: "Expense",
    chip: "bg-violet-50 text-violet-700 border-violet-200",
    approve: (id) => `/expenses/${id}/approve`,
    reject: (id) => `/expenses/${id}/reject`,
    rejectLabel: "Reject",
  },
  leave: {
    label: "Leave",
    chip: "bg-sky-50 text-sky-700 border-sky-200",
    approve: (id) => `/leaves/${id}/approve`,
    reject: (id) => `/leaves/${id}/reject`,
    rejectLabel: "Reject",
  },
  tour_plan: {
    label: "Tour plan",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    approve: (id) => `/tour-plans/${id}/approve`,
    reject: (id) => `/tour-plans/${id}/send-back`,
    rejectLabel: "Send back",
  },
  team_change: {
    label: "Team change",
    chip: "bg-teal-50 text-teal-700 border-teal-200",
    approve: (id) => `/team-changes/${id}/approve`,
    reject: (id) => `/team-changes/${id}/reject`,
    rejectLabel: "Reject",
  },
  master_change: {
    label: "Master change",
    chip: "bg-rose-50 text-rose-700 border-rose-200",
    approve: (id) => `/master-changes/${id}/approve`,
    reject: (id) => `/master-changes/${id}/reject`,
    rejectLabel: "Reject",
  },
  // A doctor quick-added from the field (§2 Session 4). Approve = verified
  // as new; Reject retires the doctor and keeps the visit; the third verb,
  // Merge, is this module's alone — see `MergeDialog` below.
  doctor_verification: {
    label: "New doctor",
    chip: "bg-orange-50 text-orange-800 border-orange-200",
    approve: (id) => `/doctor-verifications/${id}/approve`,
    reject: (id) => `/doctor-verifications/${id}/reject`,
    merge: (id) => `/doctor-verifications/${id}/merge`,
    rejectLabel: "Reject",
  },
};

/** "3 days" — how long this has been waiting, which is the only time fact that
 *  matters in a queue. An absolute date would need decoding on every row. */
function waitingFor(iso) {
  if (!iso) return "";
  // CALENDAR DAYS in IST, not elapsed milliseconds. Subtracting instants made
  // a claim filed at 23:00 IST yesterday read "today" at 09:00 this morning —
  // and the age of a queue item is the whole point of this column (RC-3).
  const filed = istDayKey(iso);
  if (!filed) return "";
  const days = diffDaysIST(filed, todayIST()) - 1;
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  if (days < 31) return `${days} days`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month" : `${months} months`;
}

/** A history row is the RECORD of a decision — it gets no buttons. An
 *  in-flight row is with somebody else, and a decided row is terminal; the
 *  only action either supports is following the detail link. */
function HistoryList({ view, rows, loading, error }) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="approvals-history-error">
        {error}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-callisto rounded-xl p-10 text-center" data-testid="approvals-history-empty">
        <p className="text-sm font-semibold text-callisto-navy">
          {view === "in_flight"
            ? "Nothing of yours is between stages."
            : "No decided items in this window."}
        </p>
        <p className="mt-1 text-xs text-callisto-muted">
          {view === "in_flight"
            ? "Claims you verify wait here until the final approver decides."
            : "Widen the dates to look further back."}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="approvals-history-list">
      {rows.map((row) => {
        const module = MODULES[row.module] || { label: row.module, chip: "" };
        const body = (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${module.chip}`}>
                {module.label}
              </span>
              <span
                className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${
                  row.my_decision === "approved"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
                data-testid={`history-my-decision-${row.id}`}
              >
                {/* What I actually did, at which stage — the reason this list
                    is worth reading (approvals.approval_history). */}
                {row.my_decision === "approved" ? "You approved" : "You rejected"}
                {row.my_stage ? ` · stage ${row.my_stage}` : ""}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-callisto-navy/15 bg-callisto-navy/5 text-callisto-navy px-2 py-0.5" data-testid={`history-status-${row.id}`}>
                {row.status}
              </span>
              {row.rework_count > 0 && (
                <span className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5">
                  Rework ×{row.rework_count}
                </span>
              )}
              <span className="text-sm font-bold text-callisto-navy truncate">{row.filer_name}</span>
            </div>
            <p className="mt-1 text-sm text-callisto-navy truncate">
              {row.title}
              {row.amount != null && <span className="ml-1.5 font-bold">{formatINR(row.amount)}</span>}
            </p>
            <p className="text-xs text-callisto-muted truncate">{row.subtitle}</p>
          </>
        );
        return row.detail_url ? (
          <Link
            key={`${row.module}-${row.id}`}
            to={row.detail_url}
            className="block bg-white border border-callisto rounded-xl p-4 hover:bg-callisto-navy/[0.02]"
            data-testid={`history-row-${row.id}`}
          >
            {body}
          </Link>
        ) : (
          <div key={`${row.module}-${row.id}`} className="bg-white border border-callisto rounded-xl p-4" data-testid={`history-row-${row.id}`}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/** The three views RC-3 asked for in one component. Pending is the queue and
 *  keeps its write actions; the other two are the record — what I signed that
 *  is still moving, and what reached a terminal state. The backend has served
 *  both since Block 2 (`/approvals/history`); no frontend consumed them, so
 *  an approver could reach the record only through the raw API (Block 5,
 *  Mala NEW-2). */
const VIEWS = [
  { key: "pending", label: "Pending" },
  { key: "in_flight", label: "In flight" },
  { key: "decided", label: "Decided" },
];

export default function ApprovalsPage() {
  // Deciding something here is the one action that reliably changes the badge,
  // so tell it rather than waiting for the next navigation.
  const { refresh: refreshBadge } = usePendingApprovals();
  const [view, setView] = useState("pending");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  // RC-12's exact complaint was a filter on the empty surface and none on the
  // full one. The history endpoint takes from/to; the queue does not need it.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rejecting, setRejecting] = useState(null); // the item being rejected
  const [remark, setRemark] = useState("");
  const [merging, setMerging] = useState(null); // the doctor_verification being merged
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/approvals/pending");
      setItems(data);
      // Drop selections for rows that are gone — otherwise a stale id could be
      // carried into the next batch and approve something the user never saw.
      setSelected((prev) => {
        const live = new Set(data.map((i) => `${i.module}:${i.id}`));
        return new Set([...prev].filter((k) => live.has(k)));
      });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      refreshBadge();
    }
  }, [refreshBadge]);

  const keyOf = (item) => `${item.module}:${item.id}`;
  const selectedItems = items.filter((i) => selected.has(keyOf(i)));
  const selectedValue = selectedItems.reduce((sum, i) => sum + (i.amount || 0), 0);

  function toggle(item) {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = keyOf(item);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map(keyOf))
    );
  }

  async function confirmBulk() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/approvals/bulk", {
        items: selectedItems.map((i) => ({ module: i.module, id: i.id })),
      });
      // A partial batch is the expected case under single-step approval, so
      // report both halves rather than calling the whole thing a success.
      if (data.approved) toast.success(`Approved ${data.approved}`);
      if (data.failed) {
        const first = data.results.find((r) => r.outcome !== "approved");
        toast.error(
          data.failed === 1
            ? first.detail
            : `${data.failed} could not be approved — ${first.detail}`
        );
      }
      setConfirmingBulk(false);
      setSelected(new Set());
      load({ quiet: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load({ quiet: true });
    } finally {
      setBulkBusy(false);
    }
  }

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async (which, from, to) => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const { data } = await api.get("/approvals/history", {
        params: { view: which, date_from: from || "", date_to: to || "" },
      });
      setHistory(data);
    } catch (e) {
      // A failed load is an error, never an empty record — "no decisions yet"
      // over a fetch that died would tell an approver their history is gone.
      setHistoryError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view !== "pending") loadHistory(view, dateFrom, dateTo);
  }, [view, dateFrom, dateTo, loadHistory]);

  async function approve(item) {
    setBusyId(item.id);
    try {
      await api.put(MODULES[item.module].approve(item.id), { remark: null });
      toast.success(`Approved · ${item.filer_name}`);
      // Optimistically drop the row so the queue keeps moving, then reconcile —
      // somebody else may have decided other rows while this one was open.
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      load({ quiet: true });
    } catch (e) {
      // The likely cause is another approver reaching it first. Refetch rather
      // than leaving a row that no longer needs anyone.
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load({ quiet: true });
    } finally {
      setBusyId(null);
    }
  }

  async function confirmMerge(intoDoctorId) {
    const item = merging;
    if (!item || !intoDoctorId) return;
    setBusyId(item.id);
    try {
      await api.put(MODULES.doctor_verification.merge(item.id), { into_doctor_id: intoDoctorId, remark: null });
      toast.success(`Merged · ${item.filer_name}`);
      setMerging(null);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      load({ quiet: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load({ quiet: true });
    } finally {
      setBusyId(null);
    }
  }

  async function confirmReject() {
    if (!rejecting) return;
    if (!remark.trim()) {
      toast.error("A reason is required");
      return;
    }
    const item = rejecting;
    setBusyId(item.id);
    try {
      await api.put(MODULES[item.module].reject(item.id), { remark: remark.trim() });
      toast.success(`${MODULES[item.module].rejectLabel} · ${item.filer_name}`);
      setRejecting(null);
      setRemark("");
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      load({ quiet: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load({ quiet: true });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <StickyHeading
        icon={Sparkles}
        label="Approvals"
        title="Waiting for you"
        subtitle={
          view === "pending"
            ? (loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"} across expenses, leaves, tour plans, team changes and master changes`)
            : view === "in_flight"
            ? "Signed by you, now with the next approver"
            : "Everything you decided that has reached its end state"
        }
        testId="approvals-heading"
        actions={
          <Button
            variant="outline"
            onClick={() => load()}
            className="h-10 border-callisto self-start"
            data-testid="approvals-refresh"
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        }
      />

      <div className="flex items-center gap-1" data-testid="approvals-views" role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            role="tab"
            aria-selected={view === v.key}
            onClick={() => setView(v.key)}
            className={`h-9 px-4 rounded-lg text-xs font-bold border transition-colors ${
              view === v.key
                ? "bg-callisto-navy text-white border-callisto-navy"
                : "bg-white text-callisto-body border-callisto hover:bg-callisto-navy/5"
            }`}
            data-testid={`approvals-view-${v.key}`}
          >
            {v.label}
          </button>
        ))}
        {view !== "pending" && (
          <div className="ml-auto flex items-center gap-2" data-testid="approvals-history-filter">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Filed from"
              className="h-9 rounded-lg border border-callisto px-2 text-xs"
              data-testid="approvals-history-from"
            />
            <span className="text-xs text-callisto-muted">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="Filed to"
              className="h-9 rounded-lg border border-callisto px-2 text-xs"
              data-testid="approvals-history-to"
            />
          </div>
        )}
      </div>

      {view !== "pending" ? (
        <HistoryList
          view={view}
          rows={history}
          loading={historyLoading}
          error={historyError}
        />
      ) : (<>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : items.length === 0 && !error ? (
        // Only when the queue is genuinely empty. A failed load also leaves
        // `items` empty, and "All caught up." would then be a lie that stops
        // someone looking for work that is actually waiting.
        <div className="bg-white border border-callisto rounded-xl p-10 text-center" data-testid="approvals-empty">
          <p className="text-sm font-semibold text-callisto-navy">All caught up.</p>
          <p className="mt-1 text-xs text-callisto-muted">
            Nothing is waiting for your decision.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="approvals-list">
          <div className="flex items-center justify-between gap-3 px-1 pb-1">
            <label className="flex items-center gap-2 text-xs text-callisto-muted cursor-pointer">
              <Checkbox
                checked={selected.size > 0 && selected.size === items.length}
                onCheckedChange={toggleAll}
                aria-label="Select all"
                data-testid="approval-select-all"
              />
              Select all
            </label>
            {selected.size > 0 && (
              <div className="flex items-center gap-3" data-testid="approvals-bulk-bar">
                <span className="text-xs text-callisto-body">
                  {selected.size} selected
                  {selectedValue > 0 && <> · {formatINR(selectedValue)}</>}
                </span>
                {/* Approve only. Rejection needs a reason per item — a shared
                    remark across unrelated rejections is not one. */}
                <Button
                  size="sm"
                  onClick={() => setConfirmingBulk(true)}
                  className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                  data-testid="approvals-bulk-approve"
                >
                  <Check size={13} className="mr-1" />
                  Approve selected
                </Button>
              </div>
            )}
          </div>

          {items.map((item) => {
            const module = MODULES[item.module];
            const busy = busyId === item.id;
            return (
              <div
                key={`${item.module}-${item.id}`}
                className="bg-white border border-callisto rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`approval-row-${item.id}`}
              >
                <Checkbox
                  checked={selected.has(keyOf(item))}
                  onCheckedChange={() => toggle(item)}
                  aria-label={`Select ${item.title} from ${item.filer_name}`}
                  className="mt-0.5 shrink-0"
                  data-testid={`approval-select-${item.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${module.chip}`}>
                      {module.label}
                    </span>
                    {/* `total_stages` is `null` when the subject's role could
                        not be resolved (see `approvals._total_stages`) —
                        hidden rather than guessed, and hidden for a genuinely
                        single-stage item (total_stages === 1) where "Stage 1
                        of 1" would just be noise. The number itself comes
                        from the backend's `final_stage`, never recomputed
                        here — reimplementing that chain in JS is exactly the
                        rule-drift that shipped two defects in #73. */}
                    {item.total_stages > 1 && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-callisto-navy/15 bg-callisto-navy/5 text-callisto-navy px-2 py-0.5"
                        data-testid={`approval-stage-${item.id}`}
                      >
                        Stage {item.stage} of {item.total_stages}
                        {item.final ? " · Final" : ""}
                      </span>
                    )}
                    {/* `amount_escalated`, never `escalated` — that field on
                        this same row is the REWORK counter, an unrelated
                        thing (see `approval_chain`'s module docstring). The
                        `module === "expense"` check is belt-and-suspenders:
                        only expenses can escalate (ESCALATING_MODULES), and
                        the backend already guarantees this, but a badge is
                        cheap insurance against a stray field on some other
                        module reading as "this is a big expense". */}
                    {item.module === "expense" && item.amount_escalated === true && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5"
                        data-testid={`approval-escalated-${item.id}`}
                      >
                        Escalated
                      </span>
                    )}
                    {/* The OTHER stamped routing cause (P7): filed outside the
                        window, taking the Director-final chain. Distinct chip,
                        same route — the reader learns WHY it is in this queue. */}
                    {item.module === "expense" && item.late_filed === true && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-purple-200 bg-purple-50 text-purple-700 px-2 py-0.5"
                        data-testid={`approval-late-${item.id}`}
                      >
                        Late filed
                      </span>
                    )}
                    {item.module === "leave" && item.retrospective === true && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-sky-200 bg-sky-50 text-sky-700 px-2 py-0.5"
                        data-testid={`approval-retrospective-${item.id}`}
                      >
                        Retrospective
                      </span>
                    )}
                    <span className="text-sm font-bold text-callisto-navy truncate">
                      {item.filer_name}
                    </span>
                    <span className="text-[11px] text-callisto-muted">
                      waiting {waitingFor(item.filed_at)}
                    </span>
                  </div>
                  {/* Team changes have no detail page — the row IS the whole
                      request, so it renders as plain text rather than a link
                      to nowhere. */}
                  {item.detail_url ? (
                    <Link
                      to={item.detail_url}
                      className="mt-1 block group"
                      data-testid={`approval-link-${item.id}`}
                    >
                      <p className="text-sm text-callisto-navy group-hover:underline truncate">
                        {item.title}
                        {item.amount != null && (
                          <span className="ml-1.5 font-bold">{formatINR(item.amount)}</span>
                        )}
                      </p>
                      <p className="text-xs text-callisto-muted truncate">{item.subtitle}</p>
                    </Link>
                  ) : (
                    <div className="mt-1" data-testid={`approval-detail-${item.id}`}>
                      <p className="text-sm text-callisto-navy truncate">{item.title}</p>
                      <p className="text-xs text-callisto-muted truncate">{item.subtitle}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => approve(item)}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                    data-testid={`approval-approve-${item.id}`}
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Check size={13} className="mr-1" /> Approve</>)}
                  </Button>
                  {item.module === "doctor_verification" && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => setMerging(item)}
                      className="h-8 border-callisto text-[11px] text-callisto-navy hover:bg-callisto-navy/5"
                      data-testid={`approval-merge-${item.id}`}
                    >
                      <Merge size={13} className="mr-1" /> Merge
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => { setRejecting(item); setRemark(""); }}
                    className="h-8 border-callisto text-[11px] text-red-700 hover:bg-red-50"
                    data-testid={`approval-reject-${item.id}`}
                  >
                    <X size={13} className="mr-1" />
                    {module.rejectLabel}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      </>)}

      <Dialog open={confirmingBulk} onOpenChange={(o) => !o && setConfirmingBulk(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve {selected.size} item{selected.size === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>
              {/* The money is what makes an expense approval consequential, so
                  it is named before the click, not after. */}
              {selectedValue > 0
                ? `Includes ${formatINR(selectedValue)} in expenses. This cannot be undone from here.`
                : "This cannot be undone from here."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingBulk(false)}>Cancel</Button>
            <Button
              onClick={confirmBulk}
              disabled={bulkBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="approvals-bulk-confirm"
            >
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MergeDialog
        item={merging}
        busy={!!busyId}
        onClose={() => setMerging(null)}
        onConfirm={confirmMerge}
      />

      <Dialog
        open={!!rejecting}
        onOpenChange={(o) => { if (!o) { setRejecting(null); setRemark(""); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rejecting ? MODULES[rejecting.module].rejectLabel : ""}
            </DialogTitle>
            <DialogDescription>
              {rejecting?.filer_name} · {rejecting?.title}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder="Reason (required)"
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="approval-remark"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setRemark(""); }}>
              Cancel
            </Button>
            <Button
              onClick={confirmReject}
              disabled={!!busyId}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="approval-reject-confirm"
            >
              {busyId ? <Loader2 className="h-4 w-4 animate-spin" /> : (rejecting ? MODULES[rejecting.module].rejectLabel : "")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Merge a quick-added doctor INTO an existing one (§2 Session 4). The survivor
 * is searched from the doctor master the approver already sees — the same
 * `/doctors?search=` the Doctors page uses, so the list here cannot offer a
 * doctor the master does not hold. Pending doctors are not in that default
 * list, and the backend refuses one as a survivor anyway.
 */
function MergeDialog({ item, busy, onClose, onConfirm }) {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    if (!item) { setQuery(""); setCandidates([]); setChosen(null); setSearchError(""); }
  }, [item]);

  useEffect(() => {
    if (!item || query.trim().length < 2) { setCandidates([]); return undefined; }
    let cancelled = false;
    api.get("/doctors", { params: { search: query.trim() } })
      .then(({ data }) => !cancelled && setCandidates((data || []).filter((d) => d.id !== item.doctor_id)))
      .catch((e) => !cancelled && setSearchError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => { cancelled = true; };
  }, [item, query]);

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge into an existing doctor</DialogTitle>
          <DialogDescription>
            {item?.title}. The visit is kept and moved to the doctor you pick; the
            new entry is retired.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setChosen(null); }}
          placeholder="Search the doctor master by name"
          className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
          data-testid="approval-merge-search"
        />
        {searchError && <p className="text-xs text-red-700">{searchError}</p>}
        <div className="max-h-56 overflow-y-auto divide-y divide-callisto border border-callisto rounded-md">
          {candidates.length === 0 ? (
            <p className="p-3 text-xs text-callisto-muted">
              {query.trim().length < 2 ? "Type at least two letters." : "No doctor matches."}
            </p>
          ) : (
            candidates.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setChosen(d)}
                className={`w-full text-left p-2.5 text-sm hover:bg-callisto-navy/5 ${chosen?.id === d.id ? "bg-callisto-gold/10" : ""}`}
                data-testid={`approval-merge-candidate-${d.id}`}
              >
                <span className="font-semibold text-callisto-navy">{d.name}</span>
                <span className="text-xs text-callisto-muted">
                  {" "}· {[d.specialization, d.territory_unit, d.assigned_to_user?.name].filter(Boolean).join(" · ")}
                </span>
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onConfirm(chosen?.id)}
            disabled={!chosen || busy}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="approval-merge-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Merge into ${chosen ? chosen.name : "…"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
